// コマンド層: アプリのすべての操作を「JSON で表現できる型付きコマンド」として登録する。
// UI (メニュー / ショートカット)、アクション記録・再生、MCP ブリッジはここを経由する。
// 各コマンドは最小限の JSON Schema を持ち、そのまま MCP のツール定義になる。

export interface JsonSchema {
  type?: 'object' | 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'null'
  properties?: Record<string, JsonSchema>
  required?: string[]
  items?: JsonSchema
  enum?: (string | number | boolean)[]
  minimum?: number
  maximum?: number
  description?: string
  additionalProperties?: boolean
  oneOf?: JsonSchema[]
  /** 文字列の正規表現 (色の #rrggbb など)。一致しなければ invalid-params */
  pattern?: string
}

export interface CommandDef<P = Record<string, unknown>, R = unknown> {
  id: string
  /** 説明 (LLM 向け、英語可) */
  description: string
  params: JsonSchema
  /** ドキュメントが開いている必要があるか */
  needsDocument?: boolean
  /** 履歴に積まない読み取り専用コマンド */
  readOnly?: boolean
  run: (params: P) => R | Promise<R>
}

export class CommandError extends Error {
  constructor(readonly code: 'unknown-command' | 'invalid-params' | 'no-document' | 'not-found' | 'failed' | 'consent-required', message: string) {
    super(message)
  }
}

const registry = new Map<string, CommandDef<never, unknown>>()

export function defineCommand<P, R>(def: CommandDef<P, R>): CommandDef<P, R> {
  if (registry.has(def.id)) throw new Error(`コマンド ID が重複: ${def.id}`)
  registry.set(def.id, def as unknown as CommandDef<never, unknown>)
  return def
}

export function listCommands(): { id: string; description: string; params: JsonSchema; needsDocument: boolean; readOnly: boolean }[] {
  return [...registry.values()].map(c => ({ id: c.id, description: c.description, params: c.params, needsDocument: !!c.needsDocument, readOnly: !!c.readOnly }))
}

export function getCommand(id: string): CommandDef<never, unknown> | undefined {
  return registry.get(id)
}

/** 最小限の JSON Schema 検証 (型・必須・enum・範囲・配列要素) */
export function validateParams(schema: JsonSchema, value: unknown, path = 'params'): string | null {
  if (schema.oneOf) {
    // 判別子 (kind / type) が一致する候補があれば、その候補だけを検査して具体的な理由を返す。
    // そうしないと「どの候補にも一致しない」だけになり、どこが悪いのか分からない。
    const branch = discriminated(schema.oneOf, value)
    if (branch) return validateParams(branch, value, path)
    for (const s of schema.oneOf) if (validateParams(s, value, path) === null) return null
    const tags = discriminatorValues(schema.oneOf)
    if (tags) return `${path}: ${tags.key} は ${tags.values.join(' | ')} のいずれかである必要がある`
    return `${path}: どの候補にも一致しない`
  }
  if (schema.enum && !schema.enum.includes(value as string)) return `${path}: ${JSON.stringify(value)} は ${schema.enum.join(' | ')} のいずれかである必要がある`
  switch (schema.type) {
    case 'object': {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return `${path}: オブジェクトが必要`
      const obj = value as Record<string, unknown>
      for (const k of schema.required ?? []) if (!(k in obj)) return `${path}.${k}: 必須`
      for (const [k, v] of Object.entries(obj)) {
        const sub = schema.properties?.[k]
        if (!sub) {
          if (schema.additionalProperties === false) return `${path}.${k}: 未知のプロパティ (使えるのは ${Object.keys(schema.properties ?? {}).join(', ') || 'なし'})`
          continue
        }
        const err = validateParams(sub, v, `${path}.${k}`)
        if (err) return err
      }
      return null
    }
    case 'array': {
      if (!Array.isArray(value)) return `${path}: 配列が必要`
      if (schema.items) for (let i = 0; i < value.length; i++) {
        const err = validateParams(schema.items, value[i], `${path}[${i}]`)
        if (err) return err
      }
      return null
    }
    case 'string':
      if (typeof value !== 'string') return `${path}: 文字列が必要`
      if (schema.pattern && !new RegExp(schema.pattern).test(value)) return `${path}: ${JSON.stringify(value)} は ${schema.pattern} に一致しない`
      return null
    case 'number':
    case 'integer': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return `${path}: 数値が必要`
      if (schema.type === 'integer' && !Number.isInteger(value)) return `${path}: 整数が必要`
      if (schema.minimum !== undefined && value < schema.minimum) return `${path}: ${schema.minimum} 以上が必要`
      if (schema.maximum !== undefined && value > schema.maximum) return `${path}: ${schema.maximum} 以下が必要`
      return null
    }
    case 'boolean':
      return typeof value === 'boolean' ? null : `${path}: 真偽値が必要`
    case 'null':
      return value === null ? null : `${path}: null が必要`
    default:
      return null
  }
}

/** oneOf の候補が kind / type の enum で分かれているとき、その値の一覧 */
function discriminatorValues(candidates: JsonSchema[]): { key: string; values: string[] } | null {
  for (const key of ['kind', 'type']) {
    const values = candidates.map(c => c.properties?.[key]?.enum).filter(e => e?.length === 1).map(e => String(e![0]))
    if (values.length === candidates.length) return { key, values }
  }
  return null
}

/** 値の kind / type に一致する候補 */
function discriminated(candidates: JsonSchema[], value: unknown): JsonSchema | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const obj = value as Record<string, unknown>
  for (const key of ['kind', 'type']) {
    const tag = obj[key]
    if (typeof tag !== 'string') continue
    const hit = candidates.find(c => c.properties?.[key]?.enum?.includes(tag))
    if (hit) return hit
  }
  return null
}

export interface CommandContext {
  hasDocument: () => boolean
}

let context: CommandContext = { hasDocument: () => false }
export function setCommandContext(ctx: CommandContext): void {
  context = ctx
}

/** コマンドを検証して実行する。失敗は CommandError */
export type CommandListener = (id: string, params: Record<string, unknown>, def: CommandDef<never, unknown>) => void
const listeners = new Set<CommandListener>()
/** コマンドが成功したあとに呼ばれる (アクション記録などに使う)。戻り値で解除 */
export function onCommandRun(fn: CommandListener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export async function runCommand(id: string, params: unknown = {}): Promise<unknown> {
  const def = registry.get(id)
  if (!def) throw new CommandError('unknown-command', `未知のコマンド: ${id}`)
  // 最上位の未知のキーは既定で弾く。layer.delete に ids ではなく id を渡すと、黙って選択中のレイヤーを消していた
  const top = def.params.type === 'object' && def.params.additionalProperties === undefined ? { ...def.params, additionalProperties: false } : def.params
  const err = validateParams(top, params ?? {})
  if (err) throw new CommandError('invalid-params', err)
  if (def.needsDocument && !context.hasDocument()) throw new CommandError('no-document', 'ドキュメントが開かれていない')
  try {
    const result = await (def.run as (p: unknown) => unknown)(params ?? {})
    for (const l of listeners) l(id, (params ?? {}) as Record<string, unknown>, def)
    return result
  } catch (e) {
    if (e instanceof CommandError) throw e
    throw new CommandError('failed', e instanceof Error ? e.message : String(e))
  }
}
